import { useEffect, useMemo, useState } from 'react';

import { GearbeltEvent, GetDocumentsRequestDTO, GetDocumentsResponseDTO, MODULE, PdfScope, RecordState, splitCategoryFilterValue } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';

import { ArchiveConfirmationDialog } from '@/components/common/archive-confirmation-dialog';
import { DeleteConfirmationDialog } from '@/components/common/delete-confirmation-dialog';
import { RestoreConfirmationDialog } from '@/components/common/restore-confirmation-dialog';
import DocumentDrawer from '@/components/documents/document-drawer';
import ArchiveIcon from '@/components/icons/archive';
import ColumnsIcon from '@/components/icons/columns';
import DeleteIcon from '@/components/icons/delete';
import DownloadCsvIcon from '@/components/icons/download-csv';
import FolderOpenIcon from '@/components/icons/folder-open';
import PlusIcon from '@/components/icons/plus';
import UndoIcon from '@/components/icons/undo';
import ColumnsDialog from '@/components/table-page/columns-dialog';
import { DownloadPdfMenu } from '@/components/table-page/download-pdf-menu';
import { TablePageHeader, TablePageHeaderActionButton } from '@/components/table-page/table-page-header';
import TablePageSearchBar from '@/components/table-page/table-page-search-bar';
import { TablePageSelectedCountIndicator } from '@/components/table-page/table-page-selected-count-indicator';
import { TablePageTitle } from '@/components/table-page/table-page-title';
import { ButtonStateToggle } from '@/components/ui/button-state-toggle';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger';
import { usePageConfig } from '@/hooks/use-page-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useResetRowSelectionOnChange } from '@/hooks/use-reset-row-selection-on-change';
import { buildFilterFields } from '@/queries/column-filter';
import { documentKeys, getDocumentsInfiniteQueryOptions } from '@/queries/documents';
import { ApiService } from '@/services/api-v2';
import { useModulesStore } from '@/stores/modules.store';
import { useEvents } from '@/utils/events';
import { openExportTab } from '@/utils/export-tab';
import { buildCsvExportFilename } from '@/utils/export-to-csv';
import { activeExportColumnIds, buildPdfExportJob, RegisterExportQuery } from '@/utils/register-pdf-export';

import { DocumentsTable, NoDocumentsCreatedDisplay, NoDocumentsFoundFromFiltersDisplay, NoDocumentsFoundFromSearchDisplay } from './table';
import { createDocumentsTableColumns, FILTER_FIELD_MAP } from './table-meta';

function DocumentsPage() {
  const queryClient = useQueryClient();
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const toast = useToast();
  const { setModuleFilters } = useModulesStore();
  const { trackEvent } = useEvents();
  const { isAdmin, canEditDocument, canExportCsv } = usePermissions();

  const {
    config: { columnOrder, grouping, sorting, filters, columnVisibility },
    loading: isLoadingPageConfig,
    updatePageConfig,
  } = usePageConfig(MODULE.Documents, {
    filterFieldMap: FILTER_FIELD_MAP,
    columns: createDocumentsTableColumns(),
  });

  const [openColumnsDialog, setOpenColumnsDialog] = useState(false);
  const [openArchiveConfirmation, setOpenArchiveConfirmation] = useState(false);
  const [openRestoreConfirmation, setOpenRestoreConfirmation] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [search, setSearch] = useState('');
  const [openDocumentDrawer, setOpenDocumentDrawer] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<GetDocumentsResponseDTO['data'][number] | null>(null);
  const [documentState, setDocumentState] = useState<RecordState>(RecordState.Active);

  const debouncedSearch = useDebounce(search, 500);

  const {
    data: documentsData,
    fetchNextPage: fetchNextDocumentsPage,
    hasNextPage: hasNextDocumentsPage,
    isFetching: isFetchingDocuments,
    isFetchingNextPage: isFetchingNextDocumentsPage,
    error: documentsError,
  } = useInfiniteQuery(
    getDocumentsInfiniteQueryOptions({
      filters: {
        state: documentState,
        search: debouncedSearch,
        ...buildFilterFields(filters, FILTER_FIELD_MAP, {
          transformValue: (columnId, rawValue) => (columnId === 'type' ? splitCategoryFilterValue(rawValue).type : rawValue),
          extraFields: { categoryId: splitCategoryFilterValue(filters?.find((filter) => filter.id === 'type')?.value).categoryId },
        }),
      },
      orderBy: sorting.key
        ? { field: sorting.key as NonNullable<GetDocumentsRequestDTO['orderBy']>['field'], direction: sorting.direction as 'asc' | 'desc' }
        : undefined,
      enabled: !isLoadingPageConfig,
    }),
  );

  const getSelectedDocuments = useMemo(() => {
    return (incomingData: typeof documentsData) => {
      if (!incomingData?.pages) return [];
      const ids = Object.keys(selectedRows);
      const documents = incomingData.pages.flatMap((page) => page.data);
      return ids.filter((id) => !isNaN(parseInt(id))).map((id) => documents[parseInt(id)]);
    };
  }, [selectedRows]);

  const { mutateAsync: archiveDocuments, isPending: isArchivingDocuments } = useMutation({
    mutationFn: async (ids: string[]) => await ApiService.BulkUpdateDocumentState({ documentIds: ids, state: RecordState.Archived }),
    onSuccess: () => {
      toast.success('Documents archived successfully');
      setOpenArchiveConfirmation(false);
      setSelectedRows({});
      // Hand-rolled setQueryData here didn't match the registered
      // `documentKeys.all` shape, so it silently
      // no-op'd. Invalidate instead.
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  function handleArchiveDocuments() {
    if (documentsData?.pages) {
      const toArchive = getSelectedDocuments(documentsData);
      archiveDocuments(toArchive.map((document) => document.id));
    }
  }

  const { mutateAsync: restoreDocuments, isPending: isRestoringDocuments } = useMutation({
    mutationFn: async (ids: string[]) => await ApiService.BulkUpdateDocumentState({ documentIds: ids, state: RecordState.Active }),
    onSuccess: () => {
      toast.success('Documents restored successfully');
      setOpenRestoreConfirmation(false);
      setSelectedRows({});
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  function handleRestoreDocuments() {
    if (documentsData?.pages) {
      const toRestore = getSelectedDocuments(documentsData);
      restoreDocuments(toRestore.map((document) => document.id));
    }
  }

  const { mutate: deleteDocuments, isPending: isDeletingDocuments } = useMutation({
    mutationFn: async (ids: string[]) => await ApiService.BulkUpdateDocumentState({ documentIds: ids, state: RecordState.Deleted }),
    onSuccess: () => {
      toast.success('Documents deleted successfully');
      setOpenDeleteConfirmation(false);
      setSelectedRows({});
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  function handleDeleteDocuments() {
    if (documentsData?.pages) {
      const toDelete = getSelectedDocuments(documentsData);
      deleteDocuments(toDelete.map((document) => document.id));
    }
  }

  const selectedDocuments = useMemo(() => getSelectedDocuments(documentsData), [documentsData, getSelectedDocuments]);

  function buildDocumentsExportRequest(): RegisterExportQuery<'documents'> {
    const selected = getSelectedDocuments(documentsData);
    return {
      queryType: 'documents',
      filters: { fields: { id: selected.map((document) => document.id) } },
      orderBy: sorting.key
        ? { field: sorting.key as NonNullable<GetDocumentsRequestDTO['orderBy']>['field'], direction: sorting.direction as 'asc' | 'desc' }
        : undefined,
      columns: [...activeExportColumnIds(columnOrder), 'url'],
    };
  }

  function handleCsvExport() {
    if (!documentsData?.pages) return;

    trackEvent(GearbeltEvent.DocumentExported, {
      Context: 'CSV',
    });

    openExportTab({ kind: 'csv', request: { ...buildDocumentsExportRequest(), filename: buildCsvExportFilename('documents') } });
  }

  function handlePdfScopeSelect(scope: PdfScope) {
    if (!documentsData?.pages) return;
    openExportTab(buildPdfExportJob({ scope, records: [], register: buildDocumentsExportRequest() }));
  }

  useEffect(() => {
    if (documentsError) {
      toast.error(documentsError.message);
    }
  }, [toast, documentsError]);

  useResetRowSelectionOnChange(setSelectedRows, [documentState, debouncedSearch, filters, sorting.key, sorting.direction, grouping]);

  const { sentinelRef, canLoadMore, loadMore } = useInfiniteScrollTrigger({
    fetchNextPage: fetchNextDocumentsPage,
    hasNextPage: hasNextDocumentsPage,
    isFetching: isFetchingDocuments,
    isError: !!documentsError,
    root: scrollRoot,
  });

  const flattenedData = useMemo(() => documentsData?.pages.flatMap((page) => page.data) ?? [], [documentsData]);

  return (
    <>
      <div className="flex flex-1 flex-col overflow-hidden">
        <TablePageHeader>
          <div className="no-scrollbar h-full w-full overflow-x-auto py-0.5">
            <div className="flex h-full min-w-[580px] items-center justify-between">
              {!selectedDocuments.length && (
                <>
                  <div className="flex h-full items-center gap-x-2 px-2">
                    <TablePageTitle>Documents</TablePageTitle>
                    <ButtonStateToggle
                      options={[
                        { icon: <FolderOpenIcon />, label: 'Active', value: RecordState.Active },
                        { icon: <ArchiveIcon />, label: 'Archived', value: RecordState.Archived },
                      ]}
                      value={documentState}
                      onChange={setDocumentState}
                      responsive
                    />
                    <TablePageSearchBar search={search} setSearch={setSearch} />
                    {filters && filters.length > 0 && (
                      <TablePageHeaderActionButton
                        icon={<UndoIcon />}
                        label="Remove all Filters"
                        kind="secondary"
                        onClick={() => setModuleFilters(MODULE.Documents, [])}
                      />
                    )}
                  </div>

                  <div className="mr-1 flex h-full items-center">
                    <TablePageHeaderActionButton
                      icon={<ColumnsIcon />}
                      label="Edit Columns"
                      disabled={isLoadingPageConfig}
                      onClick={() => setOpenColumnsDialog(true)}
                    />
                    <TablePageHeaderActionButton
                      variant="success"
                      icon={<PlusIcon />}
                      kind="primary"
                      onClick={() => setOpenDocumentDrawer(true)}
                      label="Create Document"
                    />
                  </div>
                </>
              )}

              {selectedDocuments.length > 0 && (
                <div className="flex items-center gap-1 px-2">
                  <TablePageSelectedCountIndicator selectedCount={selectedDocuments.length} totalCount={documentsData?.pages[0]?.totalCount ?? 0} />
                  {canExportCsv && <TablePageHeaderActionButton icon={<DownloadCsvIcon />} label="Download CSV" onClick={handleCsvExport} />}
                  {canExportCsv && <DownloadPdfMenu onSelect={handlePdfScopeSelect} />}

                  {isAdmin && documentState === RecordState.Active && (
                    <TablePageHeaderActionButton icon={<ArchiveIcon />} label="Archive Selected" onClick={() => setOpenArchiveConfirmation(true)} />
                  )}
                  {isAdmin && documentState === RecordState.Archived && (
                    <>
                      <TablePageHeaderActionButton icon={<UndoIcon />} label="Restore Selected" onClick={() => setOpenRestoreConfirmation(true)} />
                      <TablePageHeaderActionButton
                        icon={<DeleteIcon className="text-default-red" />}
                        label="Delete Selected"
                        onClick={() => setOpenDeleteConfirmation(true)}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </TablePageHeader>

        <ScrollArea type="always" viewportRef={setScrollRoot} className="relative flex flex-1 flex-col">
          <ScrollBar orientation="horizontal" />
          <DocumentsTable
            data={flattenedData}
            loading={(isFetchingDocuments && !isFetchingNextDocumentsPage) || isLoadingPageConfig}
            notFoundDisplay={
              debouncedSearch.length > 0 ? (
                <NoDocumentsFoundFromSearchDisplay />
              ) : filters && filters.length > 0 ? (
                <NoDocumentsFoundFromFiltersDisplay />
              ) : (
                <NoDocumentsCreatedDisplay openDocumentDrawer={() => setOpenDocumentDrawer(true)} />
              )
            }
            rowSelection={selectedRows}
            onRowSelectionChange={setSelectedRows}
            columnFilters={filters || []}
            onColumnFiltersChange={(filters) => setModuleFilters(MODULE.Documents, filters)}
            onRowClick={(row) => {
              setSelectedDocument(row);
              setOpenDocumentDrawer(true);
            }}
            sorting={sorting}
            grouping={grouping}
            columnOrder={columnOrder}
            columnVisibility={columnVisibility}
            onSortingChange={(newSorting) =>
              updatePageConfig({
                table: { sorting: newSorting },
              })
            }
          />
          {hasNextDocumentsPage && (
            <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
              {isFetchingNextDocumentsPage ? 'Loading more...' : 'Load Newer'}
            </Button>
          )}
        </ScrollArea>
      </div>

      <ColumnsDialog<GetDocumentsResponseDTO['data'][number]>
        open={openColumnsDialog}
        onOpenChange={setOpenColumnsDialog}
        columns={createDocumentsTableColumns()}
        defaultSectionLabel="Document"
        columnOrder={columnOrder}
        onColumnOrderChange={(columnOrder) => updatePageConfig({ table: { columnOrder } })}
        disabled={isLoadingPageConfig}
        noTrigger
      />

      {openDocumentDrawer && (
        <DocumentDrawer
          noTrigger
          open={openDocumentDrawer}
          onOpenChange={(open) => {
            setOpenDocumentDrawer(open);
            if (!open) setSelectedDocument(null);
          }}
          asset={selectedDocument?.asset || undefined}
          document={selectedDocument}
          readOnly={!!selectedDocument && !canEditDocument(selectedDocument.userId)}
        />
      )}

      <ArchiveConfirmationDialog
        open={openArchiveConfirmation}
        onOpenChange={setOpenArchiveConfirmation}
        callback={handleArchiveDocuments}
        loading={isArchivingDocuments}
        title="Archive documents?"
        description="Are you sure you want to archive these documents? You can restore them later from the archived tab."
      />

      <RestoreConfirmationDialog
        open={openRestoreConfirmation}
        onOpenChange={setOpenRestoreConfirmation}
        callback={handleRestoreDocuments}
        loading={isRestoringDocuments}
        title="Restore documents?"
        description="Are you sure you want to restore these documents? They will be moved back to your active documents."
      />

      <DeleteConfirmationDialog
        open={openDeleteConfirmation}
        onOpenChange={setOpenDeleteConfirmation}
        callback={handleDeleteDocuments}
        loading={isDeletingDocuments}
        title="Delete documents?"
        description="This action is permanent and cannot be undone. Are you sure you want to delete these documents?"
      />
    </>
  );
}

export default DocumentsPage;
